import { useState } from 'react';
import { fromJS } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import { TABLE } from 'reporting-data/constants/chartTypes';
import { AGGREGATION } from 'reporting-data/constants/configTypes';
import { ANALYTICS_ALL_PAGES, ANALYTICS_ALL_PAGES_SOURCES } from 'reporting-data/constants/dataTypes';
import { EQ, IN } from 'reporting-data/constants/operators';
import { ASC, DESC } from 'reporting-data/constants/sortOrder';
import { isKnownGuid } from 'reporting-data/lib/guids';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { useReportingFiltersProvider } from '../../providers/ReportingFiltersProvider';
import { addMissingProtocolToUrl } from '../../utils/urlUtils';
import SourcesTableFilterBar from './SourcesTableFilterBar';
import useReportingContentIds from '../../hooks/useReportingContentIds';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMAIL_PREVIEW_ID = '2';
const LINK_DRILLINS = ['email', 'referrals'];
const getCustomFilters = ({
  contentId,
  contentIds,
  d1,
  source
}) => {
  if (contentIds && contentIds.length > 0) {
    return [{
      operator: IN,
      property: 'filters',
      values: contentIds
    }];
  }
  const defaultFilters = [{
    operator: EQ,
    property: 'pageId',
    value: contentId
  }];
  if (!source) {
    return defaultFilters;
  }
  if (!d1) {
    defaultFilters.push({
      operator: EQ,
      property: 'd1',
      value: source
    });
    return defaultFilters;
  }
  defaultFilters.push({
    operator: EQ,
    property: 'd1',
    value: source
  }, {
    operator: EQ,
    property: 'd2',
    value: d1
  });
  return defaultFilters;
};
const getOnRowClick = ({
  d1,
  handleSourceDrilldown,
  source
}) => {
  if (source !== 'direct' && !d1) {
    return handleSourceDrilldown;
  }
  return null;
};
const handleRowLinkClick = ({
  key,
  source
}) => {
  if (isKnownGuid(key)) {
    return {};
  }
  if (source === 'email' && key === EMAIL_PREVIEW_ID) {
    return {};
  }
  if (source === 'direct') {
    return {
      external: true,
      href: addMissingProtocolToUrl(key)
    };
  }
  switch (source) {
    case 'email':
      if (!isNaN(parseFloat(key))) {
        return {};
      }
      return {
        external: true,
        href: `/email/${PortalIdParser.get()}/campaign/${key}`
      };
    case 'referrals':
      return {
        external: true,
        href: addMissingProtocolToUrl(key)
      };
    default:
      return {};
  }
};
const getOnRowLinkClick = ({
  d1,
  source
}) => {
  if (source === 'direct' || d1 && LINK_DRILLINS.includes(source)) {
    return handleRowLinkClick;
  }
  return null;
};
const getColumns = ({
  allMetricsData,
  d1,
  handleSortMetricColumn,
  handleSourceDrilldown,
  metricsToDisplay,
  source
}) => {
  const dimensionOnClick = source !== 'direct' && !d1 ? {
    breakdown: {
      onClick: ({
        value
      }) => handleSourceDrilldown({
        key: value
      })
    }
  } : {};
  return metricsToDisplay.reduce((acc, metric) => {
    const {
      tooltipKey
    } = allMetricsData[metric];
    const metricData = {
      tooltipI18nKey: tooltipKey,
      onSort: handleSortMetricColumn
    };
    acc[metric] = metricData;
    return acc;
  }, dimensionOnClick);
};
const getDisplayParams = ({
  allMetricsData,
  contentId,
  d1,
  metricsToDisplay,
  handleSortMetricColumn,
  handleSourceDrilldown,
  source,
  handleToggleSeries,
  handleToggleAllSeries,
  checkedSources
}) => {
  // const rhumbConfig = getReportRhumbConfig('SOURCES_DATA_WELLS_LOADED');
  const showCheckboxes = !source;
  if (!contentId) {
    return {
      render: {
        action: () => null
      }
      // rhumbConfig,
    };
  }
  return {
    checked: checkedSources,
    columns: getColumns({
      allMetricsData,
      d1,
      handleSortMetricColumn,
      handleSourceDrilldown,
      metricsToDisplay,
      source
    }),
    onRowClick: getOnRowClick({
      d1,
      source,
      handleSourceDrilldown
    }),
    onRowLinkClick: getOnRowLinkClick({
      d1,
      source
    }),
    render: {
      hideDataLabels: true,
      stacked: true
    },
    shouldTruncateHeader: false,
    showTotals: true,
    toggleSeries: showCheckboxes && handleToggleSeries,
    toggleAllSeries: showCheckboxes && handleToggleAllSeries
    // rhumbConfig,
  };
};
const getReport = ({
  allMetricsData,
  checkedSources,
  contentId,
  contentIds,
  dateRange,
  d1,
  handleSortMetricColumn,
  handleSourceDrilldown,
  handleToggleSeries,
  handleToggleAllSeries,
  isViewingAllVariations,
  metricsToDisplay,
  metricSortDir,
  selectedMetric,
  source
}) => {
  if (!(contentId || isViewingAllVariations)) {
    return null;
  }
  const metricsList = metricsToDisplay.map(metric => ({
    property: metric
  }));
  return {
    chartType: TABLE,
    config: {
      configType: AGGREGATION,
      dataType: isViewingAllVariations ? ANALYTICS_ALL_PAGES : ANALYTICS_ALL_PAGES_SOURCES,
      dimensions: ['breakdown'],
      filters: {
        custom: getCustomFilters({
          contentId,
          d1,
          source,
          contentIds
        }),
        dateRange: {
          property: 'sessionDate',
          value: dateRange
        }
      },
      limit: 20,
      metrics: metricsList,
      sort: [{
        order: metricSortDir,
        property: selectedMetric
      }]
    },
    displayParams: getDisplayParams({
      allMetricsData,
      d1,
      metricsToDisplay,
      handleSortMetricColumn,
      handleSourceDrilldown,
      handleToggleSeries,
      handleToggleAllSeries,
      source,
      contentId,
      checkedSources
    })
  };
};
const SourcesTable = ({
  variationId,
  variationIds
}) => {
  const {
    allMetricsData,
    dateRange,
    d1,
    metric,
    source,
    setMetric,
    handleSourceDrilldown,
    metricsToDisplay,
    checkedSources,
    setCheckedSources
  } = useReportingFiltersProvider();
  const {
    contentId,
    contentIds
  } = useReportingContentIds({
    variationId,
    variationIds
  });
  const isViewingAllVariations = Boolean(contentIds && contentIds.length > 0);
  const [metricSortDir, setMetricSortDirection] = useState(DESC);
  const handleSortMetricColumn = clickedMetric => {
    const {
      property
    } = fromMetricKey(clickedMetric);
    if (clickedMetric === metric) {
      const newSortDir = metricSortDir === ASC ? DESC : ASC;
      setMetricSortDirection(newSortDir);
    } else {
      setMetric(property);
    }
  };
  const handleToggleSeries = toggledSource => {
    const checkedSourcesList = [...checkedSources];
    const sourceInCheckedListIndex = checkedSourcesList.indexOf(toggledSource);
    if (sourceInCheckedListIndex === -1) {
      checkedSourcesList.push(toggledSource);
    } else {
      checkedSourcesList.splice(sourceInCheckedListIndex, 1);
    }
    setCheckedSources(checkedSourcesList);
  };
  const handleToggleAllSeries = updatedCheckedSourcesSet => {
    const updatedCheckedSourcesArray = Array.from(updatedCheckedSourcesSet);
    setCheckedSources(updatedCheckedSourcesArray);
  };
  const report = getReport({
    allMetricsData,
    checkedSources,
    contentId,
    contentIds,
    dateRange,
    d1,
    isViewingAllVariations,
    metricsToDisplay,
    metricSortDir,
    selectedMetric: metric,
    source,
    handleSourceDrilldown,
    handleSortMetricColumn,
    handleToggleSeries,
    handleToggleAllSeries
  });
  if (!report) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SourcesTableFilterBar, {}), /*#__PURE__*/_jsx(CardWrapper, {
      compact: true,
      children: /*#__PURE__*/_jsx(PlatformReportResolver, {
        report: fromJS(report),
        useInlineLabels: false
      })
    })]
  });
};
export default SourcesTable;